import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Pencil, Trash2, Search, Lock, RefreshCw, CheckCircle, X as XIcon, Plus } from 'lucide-react';
import { fetchRLSRules, updateRLSRule, deleteRLSRule } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

function RLSEditDialog({ open, onClose, rule, allSedes, createMode = false }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState(rule?.sedes || []);
  const [sedeSearch, setSedeSearch] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const targetEmail = createMode ? newEmail.trim().toLowerCase() : rule?.email;
  const emailValid = !createMode || /^\S+@\S+\.\S+$/.test(targetEmail);

  // allSedes comes from /api/filters/sedes → array of {value, label, code}
  const sedeCodeOf = (s) => (typeof s === 'object' ? s.code : s.split(' - ')[0].trim());
  const sedeValueOf = (s) => (typeof s === 'object' ? s.value : s);

  const filteredSedes = allSedes.filter(s => {
    const code = sedeCodeOf(s);
    const val = sedeValueOf(s).toLowerCase();
    return val.includes(sedeSearch.toLowerCase()) && !selected.includes(code);
  }).slice(0, 30);

  const updateMut = useMutation({
    mutationFn: () => updateRLSRule(targetEmail, selected),
    onSuccess: () => {
      toast.success(createMode ? `Regla RLS creada para ${targetEmail}` : `Reglas RLS actualizadas para ${rule.email}`);
      qc.invalidateQueries(['admin-rls-rules']);
      onClose();
    },
    onError: () => toast.error(createMode ? 'Error al crear la regla RLS' : 'Error al actualizar reglas RLS'),
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', maxWidth: 520 }} data-testid="rls-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield size={16} style={{ color: '#4A9EFF' }} />
            {createMode ? 'Nueva regla de acceso' : (
              <>Editar acceso: <span className="font-mono text-sm" style={{ color: '#4A9EFF' }}>{rule?.email}</span></>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {createMode && (
            <div>
              <Label className="text-xs mb-1.5 block" style={{ color: 'var(--vz-text-secondary)' }}>Email del usuario</Label>
              <Input
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="usuario@empresa.com"
                className="text-xs h-8"
                style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
                data-testid="rls-new-email-input"
              />
              {newEmail.length > 0 && !emailValid && (
                <p className="text-[10px] mt-1" style={{ color: '#FF5252' }}>Introduce un email válido</p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: 'rgba(74,158,255,0.07)', border: '1px solid rgba(74,158,255,0.15)' }}>
            <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>Sedes asignadas</p>
            <Badge style={{ background: 'rgba(74,158,255,0.15)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.3)' }}>
              {selected.length} sedes
            </Badge>
          </div>

          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto p-2 rounded-xl"
              style={{ background: 'rgba(0,200,83,0.05)', border: '1px solid rgba(0,200,83,0.12)' }}>
              {selected.map(s => (
                <button key={s} onClick={() => setSelected(prev => prev.filter(x => x !== s))}
                  className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded font-mono transition-opacity hover:opacity-70"
                  style={{ background: 'rgba(0,200,83,0.15)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}
                >
                  {s} <XIcon size={8} />
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-xs h-7 gap-1"
              style={{ color: '#FF5252', borderColor: 'rgba(255,82,82,0.3)', border: '1px solid' }}
              onClick={() => setSelected([])}>
              <XIcon size={10} /> Limpiar todo
            </Button>
            <Button size="sm" variant="ghost" className="text-xs h-7 gap-1"
              style={{ color: '#4A9EFF', borderColor: 'rgba(74,158,255,0.3)', border: '1px solid' }}
              onClick={() => setSelected(allSedes.map(s => sedeCodeOf(s)))}>
              <CheckCircle size={10} /> Seleccionar todas
            </Button>
          </div>

          <div>
            <Label className="text-xs mb-1.5 block" style={{ color: 'var(--vz-text-secondary)' }}>Agregar sedes (buscar y hacer clic)</Label>
            <Input
              value={sedeSearch}
              onChange={e => setSedeSearch(e.target.value)}
              placeholder="Buscar sede por código o nombre..."
              className="text-xs h-8 mb-2"
              style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
            />
            {sedeSearch.length > 0 && (
              <div className="grid grid-cols-2 gap-1 max-h-36 overflow-y-auto">
                {filteredSedes.length === 0
                  ? <p className="col-span-2 text-[10px] text-center py-2" style={{ color: 'var(--vz-text-muted)' }}>Sin resultados</p>
                  : filteredSedes.map(s => (
                    <button key={sedeCodeOf(s)} onClick={() => { setSelected(prev => [...prev, sedeCodeOf(s)]); setSedeSearch(''); }}
                      className="text-[10px] px-1.5 py-1 rounded text-left truncate transition-colors"
                      style={{ background: 'rgba(255,255,255,0.04)', color: '#aaa', border: '1px solid rgba(255,255,255,0.07)' }}
                      title={sedeValueOf(s)}
                    >
                      {sedeValueOf(s)}
                    </button>
                  ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button variant="ghost" size="sm" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button size="sm" className="flex-1 gap-1.5"
              style={{ background: '#4A9EFF', color: '#000' }}
              onClick={() => updateMut.mutate()}
              disabled={updateMut.isPending || !emailValid}
              data-testid="rls-save-button"
            >
              {updateMut.isPending ? 'Guardando...' : (createMode ? 'Crear regla' : 'Guardar cambios')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RLSTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editRule, setEditRule] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: rules = [], isLoading, refetch } = useQuery({
    queryKey: ['admin-rls-rules'],
    queryFn: () => fetchRLSRules(),
    staleTime: 60 * 1000,
  });
  const { data: sedesRaw = [] } = useQuery({
    queryKey: ['sedes-list'],
    queryFn: () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/filters/sedes`).then(r => r.json()),
    staleTime: 10 * 60 * 1000,
  });

  const deleteMut = useMutation({
    mutationFn: (email) => deleteRLSRule(email),
    onSuccess: (_, email) => {
      toast.success(`Acceso revocado para ${email}`);
      qc.invalidateQueries(['admin-rls-rules']);
    },
    onError: () => toast.error('Error al revocar acceso'),
  });

  const filtered = rules.filter(r => !search || r.email?.toLowerCase().includes(search.toLowerCase()));
  const totalSedes = rules.reduce((sum, r) => sum + (r.sedes?.length || 0), 0);

  return (
    <div className="space-y-4" data-testid="rls-tab">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Usuarios con acceso', value: rules.length, color: '#00C853' },
          { label: 'Asignaciones totales', value: totalSedes.toLocaleString('es-ES'), color: '#4A9EFF' },
          { label: 'Media sedes/usuario', value: rules.length ? Math.round(totalSedes / rules.length) : 0, color: '#FFD600' },
        ].map(({ label, value, color }) => (
          <div key={label} className="p-3 rounded-xl border text-center" style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)' }}>
            <p className="text-xl font-bold font-mono" style={{ color }}>{isLoading ? '—' : value}</p>
            <p className="text-[10px] mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>{label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--vz-text-muted)' }} />
          <Input
            placeholder="Buscar por email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs border"
            style={{ background: 'var(--vz-bg-elevated)', borderColor: 'var(--vz-border-subtle)', color: 'white' }}
            data-testid="rls-search-input"
          />
        </div>
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => refetch()} style={{ color: 'var(--vz-text-secondary)' }}>
          <RefreshCw size={11} /> Actualizar
        </Button>
        <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setCreateOpen(true)}
          style={{ background: '#4A9EFF', color: '#000' }} data-testid="rls-new-rule-button">
          <Plus size={13} /> Nueva regla
        </Button>
      </div>

      <ScrollArea className="h-[360px]">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {['Email', 'Sedes asignadas', 'Última actualización', 'Acciones'].map(h => (
                <TableHead key={h} className="text-[10px] uppercase tracking-wider font-montserrat" style={{ color: 'var(--vz-text-muted)' }}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  {[1,2,3,4].map(j => <TableCell key={j}><div className="h-3 rounded animate-pulse" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>)}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>
                  {search ? 'Sin resultados' : 'No hay reglas RLS configuradas.'}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((rule) => (
                <TableRow key={rule.email} className="hover:bg-[#132860] transition-colors" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                  <TableCell className="text-xs font-medium" style={{ color: '#C8D8F0' }}>{rule.email}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', border: '1px solid rgba(74,158,255,0.25)' }}>
                        <Lock size={9} /> {rule.sedes?.length ?? 0}
                      </span>
                      {rule.sedes?.slice(0, 3).map(s => (
                        <span key={s} className="text-[9px] px-1 py-0.5 rounded font-mono"
                          style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--vz-text-muted)' }}>
                          {s}
                        </span>
                      ))}
                      {(rule.sedes?.length || 0) > 3 && (
                        <span className="text-[9px]" style={{ color: 'var(--vz-text-muted)' }}>+{rule.sedes.length - 3}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[10px] font-mono" style={{ color: 'var(--vz-text-muted)' }}>
                    {rule.updated_at ? new Date(rule.updated_at).toLocaleDateString('es-ES') : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditRule(rule)} data-testid={`edit-rls-${rule.email}`}>
                        <Pencil size={11} style={{ color: '#4A9EFF' }} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6"
                        onClick={() => { if (window.confirm(`¿Revocar acceso de ${rule.email}?`)) deleteMut.mutate(rule.email); }}
                        data-testid={`delete-rls-${rule.email}`}
                      >
                        <Trash2 size={11} style={{ color: '#FF5252' }} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>

      {editRule && (
        <RLSEditDialog open={!!editRule} onClose={() => setEditRule(null)} rule={editRule} allSedes={sedesRaw} />
      )}
      {createOpen && (
        <RLSEditDialog open={createOpen} onClose={() => setCreateOpen(false)} rule={null} allSedes={sedesRaw} createMode />
      )}
    </div>
  );
}
